import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { http } from "../../services/apiClient";
import { fmtRel } from "../../utils/dates";
import { C } from "../../utils/statusMappings";
import { Icon } from "../ui/Icon";
import { Badge } from "./index";
import { useNotifs, useSearch } from "../../hooks/queries";

export const GlobalSearch = () => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const nav = useNavigate();
  const { data } = useSearch(q);
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") { e.preventDefault(); setOpen(p => !p); }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);
  if (!open) return (
    <button aria-label="Open global search" onClick={() => setOpen(true)} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 14px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 12, cursor: "pointer", fontSize: 12, color: C.mid, fontFamily: "inherit", minHeight: 38 }}>
      <Icon name="search" size={16} /> <span className="hide-mobile">Search…</span> <kbd className="hide-mobile" style={{ background: C.border, padding: "1px 5px", borderRadius: 4, fontSize: 10 }}>⌘K</kbd>
    </button>
  );
  return (
    <div onClick={e => { if (e.target === e.currentTarget) setOpen(false); }} style={{ position: "fixed", inset: 0, background: "rgba(63,35,49,0.5)", backdropFilter: "blur(8px)", zIndex: 2000, display: "flex", alignItems: "flex-start", justifyContent: "center", paddingTop: "10vh" }}>
      <div style={{ background: C.surface, borderRadius: 20, width: "100%", maxWidth: 620, boxShadow: "0 24px 80px rgba(63,35,49,0.25)", overflow: "hidden", margin: "0 16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "16px 20px", borderBottom: `1px solid ${C.border}` }}>
          <Icon name="search" size={20} style={{ color: C.secondary }} />
          <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Search customers, orders, invoices, phones…" style={{ flex: 1, border: "none", fontSize: 16, outline: "none", fontFamily: "inherit", color: C.text, background: "transparent" }} />
          <button onClick={() => setOpen(false)} style={{ background: C.bg, border: "none", borderRadius: 8, padding: "4px 8px", cursor: "pointer", color: C.mid, fontSize: 12 }}>ESC</button>
        </div>
        <div style={{ maxHeight: 440, overflowY: "auto" as any }}>
          {!q.length && <div style={{ padding: "20px 24px", fontSize: 13, color: C.light }}>Type to search across all orders, customers, and invoices</div>}
          {q.length > 1 && !data?.length && <div style={{ padding: 24, textAlign: "center" as any, color: C.mid, fontSize: 13 }}>No results for "{q}"</div>}
          {(data || []).map((r: any) => (
            <button key={r.id} onClick={() => { nav(r.entityType === "CUSTOMER" ? `/customers/${r.entityId}` : `/orders/${r.entityId}`); setOpen(false); setQ(""); }} style={{ width: "100%", textAlign: "left" as any, padding: "14px 20px", borderBottom: `1px solid ${C.bg}`, cursor: "pointer", background: "transparent", border: "none", fontFamily: "inherit", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }} onMouseEnter={e => (e.currentTarget.style.background = C.bg)} onMouseLeave={e => (e.currentTarget.style.background = "transparent")}>
              <div><div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{r.title}</div>{r.subtitle && <div style={{ fontSize: 12, color: C.mid, marginTop: 2 }}>{r.subtitle}</div>}</div>
              <Badge color={C.secondary}>{r.entityType}</Badge>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export const NotifBell = () => {
  const [open, setOpen] = useState(false);
  const { data } = useNotifs();
  const qc = useQueryClient();
  const notifs = data?.data || [];
  const unread = notifs.filter((n: any) => !n.readAt).length;
  return (
    <div style={{ position: "relative" }}>
      <button aria-label="Open notifications" onClick={() => setOpen(!open)} style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 12, width: 38, height: 38, cursor: "pointer", position: "relative", display: "flex", alignItems: "center", justifyContent: "center", color: C.chocolate }}>
        <Icon name="bell" size={17} />{unread > 0 && <span style={{ position: "absolute", top: -3, right: -3, background: C.primary, color: "#fff", fontSize: 9, fontWeight: 800, width: 17, height: 17, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>{unread > 9 ? "9+" : unread}</span>}
      </button>
      {open && (
        <div style={{ position: "absolute", right: 0, top: "calc(100% + 8px)", width: 360, background: C.surface, borderRadius: 16, boxShadow: "0 12px 40px rgba(63,35,49,0.14)", border: `1px solid ${C.border}`, zIndex: 200, overflow: "hidden" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 18px", borderBottom: `1px solid ${C.border}` }}>
            <span style={{ fontWeight: 700, fontSize: 14, color: C.text }}>Notifications</span>
            {unread > 0 && <button onClick={async () => { await http.patch("/notifications/read-all"); qc.invalidateQueries({ queryKey: ["notifs"] }); }} style={{ background: "none", border: "none", color: C.secondary, cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "inherit" }}>Mark all read</button>}
          </div>
          <div style={{ maxHeight: 400, overflowY: "auto" as any }}>
            {!notifs.length ? <div style={{ padding: 24, textAlign: "center" as any, color: C.mid, fontSize: 13 }}>All caught up ✓</div> :
              notifs.slice(0, 20).map((n: any) => (
                <div key={n.id} onClick={async () => { await http.patch(`/notifications/${n.id}/read`); qc.invalidateQueries({ queryKey: ["notifs"] }); setOpen(false); }} style={{ padding: "12px 18px", borderBottom: `1px solid ${C.bg}`, cursor: "pointer", background: !n.readAt ? C.soft : C.surface }} onMouseEnter={e => (e.currentTarget.style.background = C.bg)} onMouseLeave={e => (e.currentTarget.style.background = !n.readAt ? C.soft : C.surface)}>
                  <div style={{ fontSize: 13, fontWeight: !n.readAt ? 700 : 400, color: C.text }}>{n.title}</div>
                  <div style={{ fontSize: 12, color: C.mid, marginTop: 2, lineHeight: 1.4 }}>{n.body}</div>
                  <div style={{ fontSize: 11, color: C.light, marginTop: 3 }}>{n.createdAt ? fmtRel(n.createdAt) : ""}</div>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// 5. STATUS PIPELINE (visual tracker on every order)
